import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { Plus, Star, Truck, CheckCircle2, Package, X, ChevronDown, ChevronRight, AlertCircle, Upload, Scan, FileText, Trash2, RefreshCw, IndianRupee, CreditCard, Download, ShoppingCart, Check, Pencil } from 'lucide-react';
import ItemCodeLabel from '../components/ItemCodeLabel';
import VendorCombobox from '../components/VendorCombobox';
import ExtraChargesEditor from '../components/ExtraChargesEditor';
import RawMaterialNameAutocomplete from '../components/RawMaterialNameAutocomplete';
import { useProductCodes } from '../context/ProductCodesContext';
import { VendorModal, PaymentModal, CreatePOModal, EditBillModal } from '../components/purchases/PurchasesModals';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const downloadPdf = async (url, filename) => {
  try {
    const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename || 'document.pdf');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
  } catch (e) { toast.error('Failed to download PDF'); }
};

const PO_STATUS_STYLE = {
  'Ordered': 'bg-blue-900/40 text-blue-300 border-blue-700',
  'In Transit': 'bg-amber-900/40 text-amber-300 border-amber-700',
  'Received': 'bg-green-900/40 text-green-300 border-green-700',
  'Cancelled': 'bg-red-900/40 text-red-300 border-red-700',
};

export default function Purchases() {
  const { user } = useAuth();
  const { lookup: lookupItemCode } = useProductCodes();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const canViewCost = isAdmin || user?.role === 'manager';
  const [tab, setTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [bills, setBills] = useState([]);
  // iter208 — Vendor-advance auto-settle prompt that appears after saving
  // a purchase bill for a vendor who has an open advance balance.
  const [advancePrompt, setAdvancePrompt] = useState(null);
  const [advanceApplying, setAdvanceApplying] = useState(false);
  const [billFilters, setBillFilters] = useState({ q: '', vendor: '', lot_no: '', product: '', date_from: '', date_to: '', amount_min: '', amount_max: '' });
  // iter164 Sprint B — Edit Bill modal state
  const [editingBill, setEditingBill] = useState(null);
  const [editBillSaving, setEditBillSaving] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', contact_person: '', phone: '', email: '', gst_number: '', city: '', state: '', product_categories: '' });
  const [poForm, setPoForm] = useState({ vendor_id: '', vendor_name: '', gst_rate: 5, notes: '', items: [{ material_id: '', material_name: '', quantity: '', unit_price: '' }] });
  const [submitting, setSubmitting] = useState(false);
  // Bill scan state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [billForm, setBillForm] = useState({
    vendor_name: '', invoice_number: '', invoice_date: '', product_name: '',
    quantity: '', container_type: 'kg', weight_per_unit: '', total_weight: '',
    unit_price: '', subtotal: '', gst_rate: '', cgst_amount: '', sgst_amount: '',
    igst_amount: '', gst_amount: '', total_amount: '', gstin: '', hsn_code: '', notes: '',
    // iter153 — Multi-line array. When the user manually enters a bill,
    // billForm.items stays empty and the legacy single-product fields
    // above are used (backward compat). When a scan returns multiple
    // lines, items[] is populated and the form switches to a per-line
    // table. Each item: {product_name, quantity, container_type,
    // unit_price, subtotal, gst_rate, gst_amount, total_amount,
    // hsn_code, _suggested_raw_material, _raw_material_candidates}.
    items: [],
    // iter166 — Extra bill-level charges (transport / loading / unloading
    // / labour etc.). [{label, amount}]. Added to grand total but NOT to
    // item-level subtotal so per-line numbers stay clean.
    extra_charges: [],
  });
  const [savingBill, setSavingBill] = useState(false);
  const fileRef = useRef(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ paid_amount: '', payment_date: '', payment_method: 'NEFT' });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  // iter166 — Expand-row for saved bills list (shows items table beneath).
  const [expandedBillId, setExpandedBillId] = useState(null);

  // iter168 — Auto-calculate subtotal / gst / total from quantity, unit_price,
  // gst_rate. Used inline so typing one field instantly fills the dependent
  // ones (subtotal, gst_amount, total_amount). Operator can still hand-edit
  // any derived field — the next typing event in qty/rate/gst will overwrite.
  //
  // Robustness changes vs the original iter166 version:
  //   * Coerce numeric inputs unconditionally; treat invalid / missing as 0.
  //   * subtotal ALWAYS recomputes if qty*rate is sane (>= 0) — defensive
  //     against AI scan returning inconsistent (gst_rate=0, gst_amount=27.5)
  //     payloads that survived into state.
  //   * gst_amount ALWAYS recomputes from subtotal × gst_rate. CGST/SGST
  //     split 50/50 as intra-state default.
  //   * total ALWAYS = subtotal + gst_amount + per-line transport (if any).
  //   * Returned shape matches the input shape (numbers, not strings), and
  //     the input renderers tolerate that.
  const recalcLine = (line) => {
    const qty   = parseFloat(line.quantity);
    const rate  = parseFloat(line.unit_price);
    const gstPct = parseFloat(line.gst_rate);
    const safeQty = Number.isFinite(qty) ? qty : 0;
    const safeRate = Number.isFinite(rate) ? rate : 0;
    const safeGstPct = Number.isFinite(gstPct) ? gstPct : 0;
    const subtotal = Math.round(safeQty * safeRate * 100) / 100;
    const gstAmt = Math.round(subtotal * safeGstPct) / 100;
    const half = Math.round((gstAmt / 2) * 100) / 100;
    const total = Math.round((subtotal + gstAmt) * 100) / 100;
    return {
      ...line,
      subtotal,
      gst_amount: gstAmt,
      cgst_amount: half,
      sgst_amount: half,
      total_amount: total,
    };
  };

  // Same helper for the legacy SINGLE-line form's flat fields on billForm.
  const recalcLegacy = (form) => {
    const qty   = parseFloat(form.quantity);
    const rate  = parseFloat(form.unit_price);
    const gstPct = parseFloat(form.gst_rate);
    const safeQty = Number.isFinite(qty) ? qty : 0;
    const safeRate = Number.isFinite(rate) ? rate : 0;
    const safeGstPct = Number.isFinite(gstPct) ? gstPct : 0;
    const subtotal = Math.round(safeQty * safeRate * 100) / 100;
    const gstAmt = Math.round(subtotal * safeGstPct) / 100;
    const half = Math.round((gstAmt / 2) * 100) / 100;
    const total = Math.round((subtotal + gstAmt) * 100) / 100;
    return { ...form, subtotal, gst_amount: gstAmt, cgst_amount: half, sgst_amount: half, total_amount: total };
  };

  // Update a single line by index and re-run the line auto-calculator.
  const updateLine = (idx, patch) => setBillForm(f => ({
    ...f,
    items: f.items.map((x, i) => i === idx ? recalcLine({ ...x, ...patch }) : x),
  }));

  // Order Plan ("Need to Order Materials") state
  const [orderPlan, setOrderPlan] = useState([]);

  const loadOrderPlan = async () => {
    try {
      const res = await axios.get(`${API}/order-plan?status=pending`, { withCredentials: true });
      setOrderPlan(res.data || []);
    } catch { /* silent */ }
  };

  const markOrdered = async (op) => {
    try {
      await axios.patch(`${API}/order-plan/${op.id}`, { status: 'ordered' }, { withCredentials: true });
      toast.success(`Marked ${op.material_name} as ordered`);
      loadOrderPlan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removePlan = async (op) => {
    if (!window.confirm(`Remove ${op.material_name} from Need-to-Order list?`)) return;
    try {
      await axios.delete(`${API}/order-plan/${op.id}`, { withCredentials: true });
      toast.success('Removed');
      loadOrderPlan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createPoFromPlan = (op) => {
    // Pre-fill PO modal with this item + suggested vendor, then mark ordered after save
    setPoForm({
      vendor_id: op.suggested_vendor_id || '',
      vendor_name: op.suggested_vendor_name || '',
      gst_rate: 5,
      notes: `Auto-filled from shortage plan (batch ${op.source_ref || ''})`,
      items: [{
        material_id: op.material_id || '',
        material_name: op.material_name,
        quantity: op.qty_short,
        unit_price: op.suggested_rate || '',
      }],
      _from_plan_id: op.id,
    });
    setShowPOModal(true);
  };

  const fetchData = async () => {
    try {
      const [vRes, poRes, mRes, billRes] = await Promise.all([
        axios.get(`${API}/purchases/vendors`, { withCredentials: true }),
        axios.get(`${API}/purchases/orders`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
        axios.get(`${API}/purchases/bills`, { withCredentials: true }),
      ]);
      setVendors(vRes.data); setPos(poRes.data); setMaterials(mRes.data);
      setBills(billRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); loadOrderPlan(); }, []);
  useEffect(() => { if (tab === 'order_plan') loadOrderPlan(); }, [tab]);

  const receivePO = async (id) => {
    try {
      await axios.patch(`${API}/purchases/orders/${id}/receive`, {}, { withCredentials: true });
      fetchData();
      toast.success('PO received! Inventory updated.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updatePOStatus = async (id, status) => {
    await axios.patch(`${API}/purchases/orders/${id}/status`, { status }, { withCredentials: true });
    setPos(prev => prev.map(p => p.id === id ? { ...p, status } : p));
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/purchases/vendors`, { ...vendorForm, product_categories: vendorForm.product_categories.split(',').map(s => s.trim()).filter(Boolean) }, { withCredentials: true });
      setVendors(prev => [...prev, res.data]);
      setShowVendorModal(false);
      setVendorForm({ name: '', contact_person: '', phone: '', email: '', gst_number: '', city: '', state: '', product_categories: '' });
      toast.success('Vendor added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const addPOItem = () => setPoForm(f => ({ ...f, items: [...f.items, { material_id: '', material_name: '', quantity: '', unit_price: '' }] }));
  const removePOItem = (i) => setPoForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updatePOItem = (i, field, value) => {
    setPoForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: value };
      if (field === 'material_id') {
        const m = materials.find(m => m.id === value);
        if (m) items[i].material_name = m.name;
      }
      return { ...f, items };
    });
  };

  const handlePOSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const vendor = vendors.find(v => v.id === poForm.vendor_id);
      const res = await axios.post(`${API}/purchases/orders`, { ...poForm, vendor_name: vendor?.name || '' }, { withCredentials: true });
      setPos(prev => [res.data, ...prev]);
      setShowPOModal(false);
      setPoForm({ vendor_id: '', vendor_name: '', gst_rate: 5, notes: '', items: [{ material_id: '', material_name: '', quantity: '', unit_price: '' }] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const poSubtotal = poForm.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const poTotal = poSubtotal * (1 + poForm.gst_rate / 100);

  // Bill scan helpers
  const handleFileScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        const res = await axios.post(`${API}/purchases/bills/scan`, { image_base64: base64 }, { withCredentials: true });
        const extracted = res.data.extracted || {};
        // iter152 — store full backend response so the form can render
        // "Create Creditor?" / "Add Raw Material?" prompts when no fuzzy
        // match is found. Includes:
        //   suggested_vendor          → best vendor match if score ≥ 80
        //   suggested_raw_material    → best raw_material match if score ≥ 80
        //   vendor_candidates         → top 5 (for "Did you mean?" prompts)
        //   raw_material_candidates   → top 5
        setScanResult({
          ...extracted,
          _suggested_vendor: res.data.suggested_vendor || null,
          _suggested_raw_material: res.data.suggested_raw_material || null,
          _vendor_candidates: res.data.vendor_candidates || [],
          _raw_material_candidates: res.data.raw_material_candidates || [],
        });
        // iter153 — backend always normalises into extracted.items[] (even
        // for single-line bills). Populate billForm.items so the multi-
        // line table renders. Legacy single-product fields stay populated
        // from items[0] for any code reading the old shape.
        const items = extracted.items || [];
        const first = items[0] || {};
        setBillForm({
          vendor_name: extracted.party_name || '',
          invoice_number: extracted.invoice_number || '',
          invoice_date: extracted.invoice_date || '',
          // First-line shims (legacy)
          product_name: first.product_name || '',
          quantity: first.quantity != null ? String(first.quantity) : '',
          container_type: first.container_type || 'kg',
          weight_per_unit: first.weight_per_unit != null ? String(first.weight_per_unit) : '',
          total_weight: first.total_weight != null ? String(first.total_weight) : '',
          unit_price: first.unit_price != null ? String(first.unit_price) : '',
          subtotal: extracted.subtotal != null ? String(extracted.subtotal) : (first.subtotal != null ? String(first.subtotal) : ''),
          gst_rate: first.gst_rate != null ? String(first.gst_rate) : '',
          cgst_amount: extracted.cgst_amount != null ? String(extracted.cgst_amount) : (first.cgst_amount != null ? String(first.cgst_amount) : ''),
          sgst_amount: extracted.sgst_amount != null ? String(extracted.sgst_amount) : (first.sgst_amount != null ? String(first.sgst_amount) : ''),
          igst_amount: extracted.igst_amount != null ? String(extracted.igst_amount) : (first.igst_amount != null ? String(first.igst_amount) : ''),
          gst_amount: extracted.gst_amount != null ? String(extracted.gst_amount) : (first.gst_amount != null ? String(first.gst_amount) : ''),
          total_amount: extracted.total_amount != null ? String(extracted.total_amount) : (first.total_amount != null ? String(first.total_amount) : ''),
          gstin: extracted.gstin || '',
          hsn_code: first.hsn_code || '',
          notes: extracted.notes || '',
          // The actual multi-line source of truth.
          // iter168 — Run recalcLine on every scanned item so subtotal /
          // gst_amount / cgst / sgst / total are self-consistent on load.
          // Earlier the AI could return gst_rate=0 alongside a stale
          // gst_amount=27.5; the operator then "edited" gst_rate to 0
          // and nothing updated because onChange never fired (value
          // didn't change). Recalculating up-front kills the mismatch.
          items: items.map(it => recalcLine({
            product_name: it.product_name || '',
            quantity: it.quantity != null ? String(it.quantity) : '',
            container_type: it.container_type || 'kg',
            weight_per_unit: it.weight_per_unit != null ? String(it.weight_per_unit) : '',
            total_weight: it.total_weight != null ? String(it.total_weight) : '',
            unit_price: it.unit_price != null ? String(it.unit_price) : '',
            subtotal: it.subtotal != null ? String(it.subtotal) : '',
            gst_rate: it.gst_rate != null ? String(it.gst_rate) : '',
            cgst_amount: it.cgst_amount != null ? String(it.cgst_amount) : '',
            sgst_amount: it.sgst_amount != null ? String(it.sgst_amount) : '',
            igst_amount: it.igst_amount != null ? String(it.igst_amount) : '',
            gst_amount: it.gst_amount != null ? String(it.gst_amount) : '',
            total_amount: it.total_amount != null ? String(it.total_amount) : '',
            hsn_code: it.hsn_code || '',
            // Per-line fuzzy match (computed by backend)
            _suggested_raw_material: it._suggested_raw_material || null,
            _raw_material_candidates: it._raw_material_candidates || [],
          })),
        });
        setScanning(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
      setScanning(false);
    }
  };

  /** Create a creditor from the scanned vendor block (top-left of the bill).
   *  Fires when operator clicks the "+ Create Creditor" prompt that appears
   *  if no fuzzy match was found. Pre-fills GSTIN / address / phone / email
   *  / contact-person from the OCR result so the operator only confirms. */
  const handleCreateCreditorFromScan = async () => {
    if (!scanResult) return;
    const name = (billForm.vendor_name || scanResult.party_name || '').trim();
    if (!name) { toast.error('Vendor name is empty — fill it before creating creditor.'); return; }
    try {
      const payload = {
        name,
        gst_number: billForm.gstin || scanResult.gstin || '',
        address: scanResult.party_address || '',
        city: scanResult.party_city || '',
        state: scanResult.party_state || 'Maharashtra',
        pin_code: scanResult.party_pincode || '',
        phone: scanResult.party_phone || '',
        email: scanResult.party_email || '',
        contact_person: scanResult.party_contact_person || '',
        credit_period_days: 30,
        payment_terms: 'Net 30',
      };
      const r = await axios.post(`${API}/purchases/vendors`, payload, { withCredentials: true });
      // Update the local vendor list + ack to the operator. Clear the
      // "Create Creditor?" CTA by tagging the scan as having a match now.
      setVendors(prev => [r.data, ...prev]);
      setScanResult(prev => ({ ...prev, _suggested_vendor: { id: r.data.id, name: r.data.name, score: 100 } }));
      toast.success(`Creditor "${name}" created with GSTIN / address from the bill.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create creditor');
    }
  };

  /** Quick-create a raw material from the scanned product line. Pre-fills
   *  unit + price + HSN + gst_rate so the operator only confirms once. */
  const handleAddRawMaterialFromScan = async () => {
    if (!scanResult) return;
    const name = (billForm.product_name || scanResult.product_name || '').trim();
    if (!name) { toast.error('Product name is empty — fill it before adding raw material.'); return; }
    try {
      const payload = {
        name,
        unit: billForm.container_type || 'kg',
        // Last-paid as the starting cost — operator can refine later. The
        // FIFO lot ingestion that runs when this bill is saved will create
        // the first actual lot.
        cost_per_unit: parseFloat(billForm.unit_price) || 0,
        reorder_level: 0,
        gst_rate: parseFloat(billForm.gst_rate) || 5,
        hsn_code: billForm.hsn_code || '',
      };
      const r = await axios.post(`${API}/inventory/raw-materials`, payload, { withCredentials: true });
      setScanResult(prev => ({ ...prev, _suggested_raw_material: { id: r.data.id, name: r.data.name, score: 100 } }));
      toast.success(`Raw material "${name}" added to inventory.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add raw material');
    }
  };

  const handleSaveBill = async (e) => {
    e.preventDefault(); setSavingBill(true);
    try {
      // iter153 — multi-line submit. If billForm.items has entries, we
      // send the items[] array (backend creates one purchase_bills row per
      // line + a single combined voucher). Otherwise we fall through to
      // the legacy single-product fields.
      const payload = { ...billForm };
      if (billForm.items && billForm.items.length > 0) {
        // Strip internal fuzzy-match keys before submit — backend doesn't
        // need them and they'd pollute the stored bill.
        payload.items = billForm.items.map(({ _suggested_raw_material, _raw_material_candidates, ...rest }) => rest);
      } else {
        delete payload.items;
      }
      const res = await axios.post(`${API}/purchases/bills`, payload, { withCredentials: true });
      // iter208 — Capture freshly-saved bill IDs so we can check vendor
      // advance balance and offer to auto-settle in a follow-up popup.
      const newBills = res.data?.multi_line ? (res.data.bills || []) : (res.data ? [res.data] : []);
      if (res.data && res.data.multi_line) {
        setBills(prev => [...res.data.bills, ...prev]);
        toast.success(`${res.data.line_count} bills saved & linked to cashflow (₹${res.data.total_amount.toFixed(0)} total)`);
      } else {
        setBills(prev => [res.data, ...prev]);
        toast.success('Bill saved & linked to cashflow!');
      }
      // Check the first saved bill's vendor for advance balance. If present,
      // prompt the operator to apply it now.
      const probe = newBills[0];
      if (probe?.id) {
        try {
          const adv = await axios.get(`${API}/purchases/bills/${probe.id}/advance-available`, { withCredentials: true });
          if (adv.data && adv.data.suggested_apply > 0) {
            setAdvancePrompt({
              billId: probe.id,
              billNumber: probe.invoice_number || probe.bill_number || '',
              vendorName: adv.data.vendor_name,
              advanceBalance: adv.data.advance_balance,
              billOutstanding: adv.data.bill_outstanding,
              suggested: adv.data.suggested_apply,
            });
          }
        } catch { /* advance probe is opportunistic — never block bill save */ }
      }
      setScanResult(null);
      setBillForm({
        vendor_name: '', invoice_number: '', invoice_date: '', product_name: '',
        quantity: '', container_type: 'kg', weight_per_unit: '', total_weight: '',
        unit_price: '', subtotal: '', gst_rate: '', cgst_amount: '', sgst_amount: '',
        igst_amount: '', gst_amount: '', total_amount: '', gstin: '', hsn_code: '', notes: '',
        items: [],
        extra_charges: [],
      });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving bill'); }
    finally { setSavingBill(false); }
  };

  // deleteBill now uses useAdminDelete hook via requestDelete (see button onClick handlers)

  const openPaymentModal = (bill) => {
    setPaymentModal(bill);
    setPaymentForm({
      paid_amount: String(bill.paid_amount || bill.total_amount || ''),
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: bill.payment_method || 'NEFT',
    });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault(); setSubmittingPayment(true);
    try {
      const res = await axios.put(
        `${API}/purchases/bills/${paymentModal.id}/payment`,
        paymentForm,
        { withCredentials: true }
      );
      setBills(prev => prev.map(b => b.id === paymentModal.id ? res.data : b));
      setPaymentModal(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error recording payment'); }
    finally { setSubmittingPayment(false); }
  };

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Purchases & Vendors</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{vendors.length} vendors · {pos.length} orders · {bills.length} bills</p>
        </div>
        <button data-testid="create-po-btn" onClick={() => setShowPOModal(true)} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
          <Plus size={14} /> New Purchase Order
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit">
        {['vendors', 'orders', 'bills', 'order_plan', 'auto_po'].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-xs font-medium capitalize transition-all ${tab === t ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
            {t === 'orders' ? 'Purchase Orders' : t === 'bills' ? 'Purchase Bills' : t === 'order_plan' ? 'Need to Order' : t === 'auto_po' ? 'Auto-PO' : 'Vendors'}
            {t === 'order_plan' && orderPlan.filter(o => o.status === 'pending').length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold">{orderPlan.filter(o => o.status === 'pending').length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══ NEED TO ORDER MATERIALS TAB ══ */}
      {tab === 'order_plan' && (
        <div data-testid="order-plan-tab">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-1.5">
                  <ShoppingCart size={14} className="text-amber-400" /> Need to Order Materials
                </h3>
                <p className="text-zinc-500 text-[11px] mt-0.5">Auto-populated when production encounters a shortage · click <Check size={10} className="inline" /> to mark ordered or open a draft PO</p>
              </div>
              <button onClick={loadOrderPlan} title="Refresh"
                data-testid="order-plan-refresh"
                className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-zinc-200">
                <RefreshCw size={13} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-zinc-950/50 border-b border-zinc-800">
                  <tr>
                    {['Material', 'Short Qty', 'Suggested Vendor', 'Rate', 'Lead Time', 'Source', 'Actions'].map(h => (
                      <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2.5 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderPlan.filter(o => o.status === 'pending').length === 0 && (
                    <tr><td colSpan={7} className="p-10 text-center text-zinc-600 text-xs">No pending items — all caught up.</td></tr>
                  )}
                  {orderPlan.filter(o => o.status === 'pending').map(op => (
                    <tr key={op.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`order-plan-row-${op.id}`}>
                      <td className="px-3 py-2.5 text-zinc-100 text-xs font-medium whitespace-nowrap"><ItemCodeLabel name={op.material_name} /></td>
                      <td className="px-3 py-2.5 font-mono text-amber-300 text-xs font-semibold whitespace-nowrap">{op.qty_short} {op.unit}</td>
                      <td className="px-3 py-2.5 text-zinc-300 text-xs whitespace-nowrap max-w-[180px] truncate" title={op.suggested_vendor_name}>
                        {op.suggested_vendor_name || <span className="text-zinc-600 italic">— no history —</span>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-zinc-300 text-xs whitespace-nowrap">
                        {op.suggested_rate ? `₹${op.suggested_rate}` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-400 text-xs whitespace-nowrap">
                        {op.suggested_lead_time_days ? `${op.suggested_lead_time_days} d` : '—'}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${op.source === 'production' ? 'bg-amber-500/15 text-amber-300 border-amber-700' : op.source === 'chat' ? 'bg-blue-500/15 text-blue-300 border-blue-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                          {op.source}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => markOrdered(op)}
                            data-testid={`order-plan-tick-${op.id}`}
                            title="Mark as ordered"
                            className="p-1.5 rounded bg-green-500/15 text-green-300 hover:bg-green-500/30">
                            <Check size={12} />
                          </button>
                          <button
                            onClick={() => createPoFromPlan(op)}
                            data-testid={`order-plan-draft-${op.id}`}
                            title="Open new PO pre-filled with this item"
                            className="px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black text-[10px] font-semibold">
                            Draft PO
                          </button>
                          <button
                            onClick={() => removePlan(op)}
                            data-testid={`order-plan-remove-${op.id}`}
                            title="Remove from plan"
                            className="p-1.5 rounded bg-red-900/30 text-red-400 hover:text-red-300">
                            <X size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Vendors Grid */}
      {tab === 'vendors' && (
        <div>
          <div className="flex justify-end mb-4">
            <button data-testid="add-vendor-btn" onClick={() => setShowVendorModal(true)} className="flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs px-3 py-2 rounded-xl transition-colors"><Plus size={13} /> Add Vendor</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(v => (
              <div key={v.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:-translate-y-0.5 transition-transform" data-testid={`vendor-card-${v.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-semibold text-zinc-50 text-sm">{v.name}</h3>
                    <p className="text-zinc-500 text-xs mt-0.5">{v.city}, {v.state}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-amber-500/10 px-2 py-1 rounded-lg">
                    <Star size={11} className="text-amber-400 fill-amber-400" />
                    <span className="text-amber-400 text-xs font-semibold">{v.rating}</span>
                  </div>
                </div>
                <div className="space-y-2 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500 flex items-center gap-1"><Truck size={11} /> On-time Delivery</span>
                    <span className={`font-semibold ${v.on_time_delivery >= 90 ? 'text-green-400' : 'text-yellow-400'}`}>{v.on_time_delivery}%</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${v.on_time_delivery >= 90 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${v.on_time_delivery}%` }} />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Defect Rate</span>
                    <span className={`font-semibold ${v.defect_rate <= 2 ? 'text-green-400' : 'text-red-400'}`}>{v.defect_rate}%</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(v.product_categories || []).slice(0, 3).map(c => (
                    <span key={c} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500 flex items-center justify-between">
                  <div><span>{v.contact_person}</span> · <span>{v.phone}</span></div>
                  {isAdmin && <button onClick={() => requestDelete(`/purchases/vendors/${v.id}`, v.name, `Delete vendor "${v.name}" permanently.`)} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={12} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Purchase Orders Table */}
      {tab === 'orders' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="border-b border-zinc-800">
              {(canViewCost
                ? ['PO ID', 'Vendor', 'Items', 'Subtotal', 'GST', 'Total', 'Order Date', 'Expected', 'Status', 'Action']
                : ['PO ID', 'Vendor', 'Items', 'Order Date', 'Expected', 'Status', 'Action']).map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {pos.map(po => (
                <tr key={po.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`po-row-${po.id}`}>
                  <td className="px-4 py-3 font-mono text-amber-400 text-xs">{po.id}</td>
                  <td className="px-4 py-3 text-zinc-200 text-xs">{po.vendor_name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{po.items?.length} item(s)</td>
                  {canViewCost && <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{fmt(po.subtotal)}</td>}
                  {canViewCost && <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{fmt(po.gst_amount)}</td>}
                  {canViewCost && <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold">{fmt(po.total_amount)}</td>}
                  <td className="px-4 py-3 text-zinc-500 text-xs">{po.order_date?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{po.expected_delivery?.slice(0, 10) || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${PO_STATUS_STYLE[po.status] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>{po.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`po-pdf-${po.id}`} onClick={() => downloadPdf(`${API}/purchases/orders/${po.id}/pdf`, `PO_${po.id}.pdf`)} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-zinc-700 px-2 py-1 rounded-lg flex items-center gap-1 transition-colors" title="Download PO PDF">
                        <FileText size={11} /> PDF
                      </button>
                      {po.status !== 'Received' && po.status !== 'Cancelled' && (
                        <button data-testid={`receive-po-${po.id}`} onClick={() => receivePO(po.id)} className="text-xs bg-green-900/40 hover:bg-green-800/50 text-green-300 border border-green-700 px-2 py-1 rounded-lg flex items-center gap-1 transition-colors">
                          <CheckCircle2 size={11} /> Receive
                        </button>
                      )}
                      {isAdmin && <button onClick={() => requestDelete(`/purchases/orders/${po.id}`, po.id, `Delete this PO permanently.`)} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={12} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pos.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No purchase orders yet</div>}
        </div>
      )}

      {/* Bills Tab */}
      {tab === 'bills' && (
        <div className="space-y-4">
          {/* Scan Invoice Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Scan size={18} className="text-amber-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">Scan Vendor Bill</h3>
                <p className="text-zinc-500 text-xs">Upload a vendor invoice image — AI extracts all details automatically</p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileScan} className="hidden" data-testid="bill-file-input" />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="scan-bill-btn"
                onClick={() => fileRef.current?.click()}
                disabled={scanning}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
              >
                {scanning ? <RefreshCw size={15} className="animate-spin" /> : <Upload size={15} />}
                {scanning ? 'Scanning with AI...' : 'Upload & Scan Bill'}
              </button>
              <button
                data-testid="manual-bill-btn"
                type="button"
                onClick={() => {
                  setScanResult(null);
                  setBillForm({
                    vendor_name: 'New', invoice_number: '', invoice_date: new Date().toISOString().slice(0, 10),
                    product_name: '', quantity: '', container_type: 'kg', weight_per_unit: '', total_weight: '',
                    unit_price: '', subtotal: '', gst_rate: '', cgst_amount: '', sgst_amount: '', igst_amount: '',
                    gst_amount: '', total_amount: '', gstin: '', hsn_code: '', notes: '',
                    items: [{ product_name: '', quantity: '', container_type: 'kg', unit_price: '', subtotal: '', gst_rate: '5', gst_amount: '', total_amount: '', hsn_code: '', lot_no: '', receiving_date: new Date().toISOString().slice(0, 10) }],
                    extra_charges: [],
                  });
                  // Clear the placeholder so VendorCombobox starts empty after mount
                  setTimeout(() => setBillForm(f => ({ ...f, vendor_name: '' })), 0);
                }}
                className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors">
                <Plus size={15} /> Manual Entry
              </button>
            </div>
          </div>

          {/* Bill Form (shown after scan or after Manual Entry click) */}
          {(scanResult !== null || billForm.vendor_name || (billForm.items && billForm.items.length > 0)) && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">
                  {scanResult ? 'AI-Extracted Bill Details' : 'Manual Bill Entry'}
                  {scanResult && <span className="ml-2 text-xs text-green-400 font-normal">Review & edit before saving</span>}
                </h3>
                <button onClick={() => { setScanResult(null); setBillForm({ vendor_name: '', invoice_number: '', invoice_date: '', product_name: '', quantity: '', container_type: 'kg', weight_per_unit: '', total_weight: '', unit_price: '', subtotal: '', gst_rate: '', cgst_amount: '', sgst_amount: '', igst_amount: '', gst_amount: '', total_amount: '', gstin: '', hsn_code: '', notes: '' }); }}
                  className="text-zinc-500 hover:text-zinc-200"><X size={15} /></button>
              </div>
              <form onSubmit={handleSaveBill} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Vendor / Party Name *</label>
                    <VendorCombobox
                      testid="bill-vendor-combobox"
                      value={billForm.vendor_name}
                      onChange={v => setBillForm(f => ({ ...f, vendor_name: v }))}
                      onSelect={v => setBillForm(f => ({
                        ...f,
                        vendor_name: v.name || '',
                        gstin: v.gstin || f.gstin,
                        vendor_id: v.id,
                      }))}
                      placeholder="Search vendor or add new…"
                    />
                    {/* iter152 — keep the old "Create Creditor?" shortcut
                        for the AI scan flow when fuzzy match misses. */}
                    {scanResult && !scanResult._suggested_vendor && billForm.vendor_name && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        <span className="text-amber-300 text-[11px]">No creditor matches "{billForm.vendor_name}".</span>
                        {(scanResult._vendor_candidates || []).slice(0, 3).filter(c => c.score >= 50).map(c => (
                          <button key={c.id} type="button"
                            onClick={() => { setBillForm(f => ({ ...f, vendor_name: c.name })); setScanResult(prev => ({ ...prev, _suggested_vendor: c })); }}
                            data-testid={`vendor-candidate-${c.id}`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700">
                            Did you mean: {c.name} ({c.score}%)?
                          </button>
                        ))}
                        <button type="button" onClick={handleCreateCreditorFromScan}
                          data-testid="create-creditor-from-scan"
                          className="ml-auto text-[11px] px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                          + Create Creditor
                        </button>
                      </div>
                    )}
                    {scanResult && scanResult._suggested_vendor && (
                      <div className="mt-1 text-[10px] text-green-400">✓ Linked to: {scanResult._suggested_vendor.name}</div>
                    )}
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Invoice Number</label>
                    <input value={billForm.invoice_number} onChange={e => setBillForm(f => ({ ...f, invoice_number: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Invoice Date</label>
                    <input type="date" value={billForm.invoice_date} onChange={e => setBillForm(f => ({ ...f, invoice_date: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Vendor GSTIN</label>
                    <input value={billForm.gstin} onChange={e => setBillForm(f => ({ ...f, gstin: e.target.value }))}
                      placeholder="27AABCX..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  {/* ─── iter153 Multi-line table ─────────────────────
                       When the scan returned 2+ items[], hide the legacy
                       single-product fields and render a per-line table.
                       Single-line bills (items.length===1 OR manual entry)
                       keep the original UI. */}
                  {billForm.items && billForm.items.length > 0 ? (
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-zinc-300 text-xs font-semibold">
                          {billForm.items.length === 1 ? '1 line item' : `${billForm.items.length} line items`}
                        </div>
                        <button type="button"
                          onClick={() => setBillForm(f => ({ ...f, items: [...(f.items || []), { product_name: '', quantity: '', container_type: 'kg', unit_price: '', subtotal: '', gst_rate: '5', gst_amount: '', total_amount: '', hsn_code: '', lot_no: '', receiving_date: f.invoice_date || new Date().toISOString().slice(0,10) }] }))}
                          data-testid="add-line-item"
                          className="text-[11px] px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700">
                          + Add Line
                        </button>
                      </div>
                      <div className="overflow-x-auto border border-zinc-800 rounded-xl">
                        <table className="w-full text-xs" data-testid="bill-items-table">
                          <thead className="bg-zinc-900">
                            <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                              <th className="px-2 py-2 text-left">#</th>
                              <th className="px-2 py-2 text-left min-w-[180px]">Product / Material</th>
                              <th className="px-2 py-2 text-left">Qty</th>
                              <th className="px-2 py-2 text-left">Unit</th>
                              <th className="px-2 py-2 text-left">Rate ₹</th>
                              <th className="px-2 py-2 text-left">Subtotal ₹</th>
                              <th className="px-2 py-2 text-left">GST %</th>
                              <th className="px-2 py-2 text-left">GST ₹</th>
                              <th className="px-2 py-2 text-left">Total ₹</th>
                              <th className="px-2 py-2 text-left">HSN</th>
                              <th className="px-2 py-2 text-left">Lot No</th>
                              <th className="px-2 py-2 text-left">Recv. Date</th>
                              <th className="px-2 py-2 text-left"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {billForm.items.map((it, idx) => (
                              <React.Fragment key={idx}>
                                <tr className="border-t border-zinc-800" data-testid={`bill-item-row-${idx}`}>
                                  <td className="px-2 py-1.5 text-zinc-500">{idx + 1}</td>
                                  <td className="px-2 py-1.5">
                                    <RawMaterialNameAutocomplete
                                      value={it.product_name}
                                      materials={materials}
                                      onChange={name => updateLine(idx, { product_name: name })}
                                      onLastPrice={(hit) => {
                                        // Only auto-fill if user hasn't already typed a rate.
                                        const currentRate = parseFloat(it.unit_price) || 0;
                                        if (currentRate === 0 && hit?.last_rate > 0) {
                                          updateLine(idx, { unit_price: hit.last_rate });
                                        }
                                      }}
                                      testId={`line-product-${idx}`}
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.quantity}
                                      onChange={e => updateLine(idx, { quantity: e.target.value })}
                                      data-testid={`line-qty-${idx}`}
                                      className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <select value={it.container_type || 'kg'}
                                      onChange={e => updateLine(idx, { container_type: e.target.value })}
                                      className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500">
                                      {['kg', 'box', 'drum', 'bag', 'pcs', 'ltr'].map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.unit_price}
                                      onChange={e => updateLine(idx, { unit_price: e.target.value })}
                                      data-testid={`line-rate-${idx}`}
                                      className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.subtotal}
                                      readOnly tabIndex={-1}
                                      data-testid={`line-subtotal-${idx}`}
                                      title="Auto-calculated · qty × rate"
                                      className="w-24 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-emerald-300 font-mono cursor-not-allowed" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.gst_rate}
                                      onChange={e => updateLine(idx, { gst_rate: e.target.value })}
                                      onBlur={e => updateLine(idx, { gst_rate: e.target.value })}
                                      data-testid={`line-gst-rate-${idx}`}
                                      className="w-16 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.gst_amount}
                                      readOnly tabIndex={-1}
                                      data-testid={`line-gst-amt-${idx}`}
                                      title="Auto-calculated · subtotal × gst%"
                                      className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-emerald-300 font-mono cursor-not-allowed" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" value={it.total_amount}
                                      readOnly tabIndex={-1}
                                      data-testid={`line-total-${idx}`}
                                      title="Auto-calculated · subtotal + gst"
                                      className="w-24 bg-zinc-950 border border-amber-500/40 rounded px-2 py-1 text-amber-400 font-semibold cursor-not-allowed" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input value={it.hsn_code || ''}
                                      onChange={e => setBillForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, hsn_code: e.target.value } : x) }))}
                                      className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input value={it.lot_no || ''}
                                      onChange={e => setBillForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, lot_no: e.target.value } : x) }))}
                                      data-testid={`line-lot-no-${idx}`}
                                      placeholder="auto"
                                      className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="date" value={it.receiving_date || billForm.invoice_date || new Date().toISOString().slice(0,10)}
                                      onChange={e => setBillForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, receiving_date: e.target.value } : x) }))}
                                      data-testid={`line-recv-date-${idx}`}
                                      className="w-32 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500" />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    {billForm.items.length > 1 && (
                                      <button type="button"
                                        onClick={() => setBillForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                                        data-testid={`remove-line-${idx}`}
                                        className="p-1 rounded bg-red-900/30 text-red-400 hover:bg-red-900/50">
                                        <X size={11} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                {/* Per-line "Did you mean?" / + Add Raw Material prompt */}
                                {!it._suggested_raw_material && it.product_name && (it._raw_material_candidates || []).length > 0 && (
                                  <tr className="bg-sky-500/5">
                                    <td colSpan={13} className="px-2 py-1.5">
                                      <div className="flex flex-wrap items-center gap-2 text-[10px]">
                                        <span className="text-sky-300">Line {idx + 1}: no raw material matches "{it.product_name}".</span>
                                        {(it._raw_material_candidates || []).slice(0, 3).filter(c => c.score >= 50).map(c => (
                                          <button key={c.id} type="button"
                                            onClick={() => setBillForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, product_name: c.name, _suggested_raw_material: c } : x) }))}
                                            data-testid={`line-${idx}-rm-cand-${c.id}`}
                                            className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700">
                                            Did you mean: {c.name} ({c.score}%)?
                                          </button>
                                        ))}
                                        <button type="button"
                                          onClick={async () => {
                                            try {
                                              const r = await axios.post(`${API}/inventory/raw-materials`, {
                                                name: it.product_name,
                                                unit: it.container_type || 'kg',
                                                cost_per_unit: parseFloat(it.unit_price) || 0,
                                                reorder_level: 0,
                                                gst_rate: parseFloat(it.gst_rate) || 5,
                                                hsn_code: it.hsn_code || '',
                                              }, { withCredentials: true });
                                              setBillForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, _suggested_raw_material: { id: r.data.id, name: r.data.name, score: 100 } } : x) }));
                                              toast.success(`Raw material "${it.product_name}" added.`);
                                            } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
                                          }}
                                          data-testid={`line-${idx}-add-rm`}
                                          className="ml-auto px-2 py-0.5 rounded bg-sky-500 hover:bg-sky-400 text-black font-semibold">
                                          + Add Raw Material
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {it._suggested_raw_material && (
                                  <tr className="bg-green-500/5">
                                    <td colSpan={11} className="px-2 py-1 text-[10px] text-green-400">
                                      Line {idx + 1}: ✓ Linked to {it._suggested_raw_material.name}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                          <tfoot className="bg-zinc-900 border-t-2 border-zinc-700">
                            <tr className="text-zinc-300 text-xs font-semibold">
                              <td colSpan={5} className="px-2 py-2 text-right">Items Totals</td>
                              <td className="px-2 py-2">
                                ₹{billForm.items.reduce((s, x) => s + (parseFloat(x.subtotal) || 0), 0).toFixed(2)}
                              </td>
                              <td></td>
                              <td className="px-2 py-2">
                                ₹{billForm.items.reduce((s, x) => s + (parseFloat(x.gst_amount) || 0), 0).toFixed(2)}
                              </td>
                              <td className="px-2 py-2 text-amber-400 font-bold" data-testid="bill-items-grand-total">
                                ₹{billForm.items.reduce((s, x) => s + (parseFloat(x.total_amount) || 0), 0).toFixed(2)}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {/* iter166 — Bill-level extra charges */}
                      <ExtraChargesEditor
                        charges={billForm.extra_charges}
                        onChange={(charges) => setBillForm(f => ({ ...f, extra_charges: charges }))}
                        itemsTotal={billForm.items.reduce((s, x) => s + (parseFloat(x.total_amount) || 0), 0)}
                      />
                      <div className="mt-3">
                        <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                        <input value={billForm.notes} onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                      </div>
                    </div>
                  ) : (
                  <>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Product / Material</label>
                    <input value={billForm.product_name} onChange={e => setBillForm(f => ({ ...f, product_name: e.target.value }))}
                      data-testid="bill-product-name"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    {/* iter152 — "Add Raw Material?" appears when the OCR'd
                        product name didn't match any existing raw_material.
                        Clicking creates the raw material with the scanned
                        unit, price, HSN, GST so the operator only confirms. */}
                    {scanResult && !scanResult._suggested_raw_material && billForm.product_name && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2">
                        <span className="text-sky-300 text-[11px]">No raw material matches "{billForm.product_name}".</span>
                        {(scanResult._raw_material_candidates || []).slice(0, 3).filter(c => c.score >= 50).map(c => (
                          <button key={c.id} type="button"
                            onClick={() => { setBillForm(f => ({ ...f, product_name: c.name })); setScanResult(prev => ({ ...prev, _suggested_raw_material: c })); }}
                            data-testid={`rm-candidate-${c.id}`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700">
                            Did you mean: {c.name} ({c.score}%)?
                          </button>
                        ))}
                        <button type="button" onClick={handleAddRawMaterialFromScan}
                          data-testid="add-raw-material-from-scan"
                          className="ml-auto text-[11px] px-2.5 py-1 rounded bg-sky-500 hover:bg-sky-400 text-black font-semibold">
                          + Add Raw Material
                        </button>
                      </div>
                    )}
                    {scanResult && scanResult._suggested_raw_material && (
                      <div className="mt-1 text-[10px] text-green-400">✓ Linked to: {scanResult._suggested_raw_material.name}</div>
                    )}
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Container Type</label>
                    <select value={billForm.container_type} onChange={e => setBillForm(f => ({ ...f, container_type: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                      {['kg', 'box', 'drum', 'bag', 'pcs'].map(t => <option key={t} value={t} className="bg-zinc-900">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Quantity (No. of {billForm.container_type}s)</label>
                    <input type="number" value={billForm.quantity} onChange={e => setBillForm(f => recalcLegacy({ ...f, quantity: e.target.value }))}
                      data-testid="bill-qty"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Weight per {billForm.container_type} (kg)</label>
                    <input type="number" step="any" value={billForm.weight_per_unit} onChange={e => setBillForm(f => ({ ...f, weight_per_unit: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Unit Price (₹)</label>
                    <input type="number" step="any" value={billForm.unit_price} onChange={e => setBillForm(f => recalcLegacy({ ...f, unit_price: e.target.value }))}
                      data-testid="bill-rate"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Subtotal (₹)</label>
                    <input type="number" step="any" value={billForm.subtotal}
                      readOnly tabIndex={-1}
                      data-testid="bill-subtotal"
                      title="Auto-calculated · qty × rate"
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-emerald-300 text-sm font-mono cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">GST Rate (%)</label>
                    <input type="number" value={billForm.gst_rate}
                      onChange={e => setBillForm(f => recalcLegacy({ ...f, gst_rate: e.target.value }))}
                      onBlur={e => setBillForm(f => recalcLegacy({ ...f, gst_rate: e.target.value }))}
                      data-testid="bill-gst-rate"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">CGST Amount (₹)</label>
                    <input type="number" step="any" value={billForm.cgst_amount}
                      readOnly tabIndex={-1}
                      title="Auto-calculated · gst_amount ÷ 2"
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-emerald-300 text-sm font-mono cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">SGST Amount (₹)</label>
                    <input type="number" step="any" value={billForm.sgst_amount}
                      readOnly tabIndex={-1}
                      title="Auto-calculated · gst_amount ÷ 2"
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-emerald-300 text-sm font-mono cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Total GST (₹)</label>
                    <input type="number" step="any" value={billForm.gst_amount}
                      readOnly tabIndex={-1}
                      data-testid="bill-gst-amount"
                      title="Auto-calculated · subtotal × gst%"
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-emerald-300 text-sm font-mono cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">Total Amount (₹) *</label>
                    <input required type="number" step="any" value={billForm.total_amount}
                      readOnly tabIndex={-1}
                      data-testid="bill-total"
                      title="Auto-calculated · subtotal + GST + extras"
                      className="w-full bg-zinc-950 border border-amber-500/50 rounded-xl px-3 py-2.5 text-amber-400 text-sm font-semibold cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs mb-1.5 block">HSN Code</label>
                    <input value={billForm.hsn_code} onChange={e => setBillForm(f => ({ ...f, hsn_code: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                    <input value={billForm.notes} onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  {/* iter166 — Extra bill-level charges on legacy single-line bill */}
                  <div className="col-span-2">
                    <ExtraChargesEditor
                      charges={billForm.extra_charges}
                      onChange={(charges) => {
                        const extrasTot = (charges || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
                        const itemTot = parseFloat(billForm.subtotal || 0) + parseFloat(billForm.gst_amount || 0);
                        const newTotal = Math.round((itemTot + extrasTot) * 100) / 100;
                        setBillForm(f => ({ ...f, extra_charges: charges, total_amount: newTotal }));
                      }}
                      itemsTotal={parseFloat(billForm.subtotal || 0) + parseFloat(billForm.gst_amount || 0)}
                    />
                  </div>
                  </>
                  )}
                </div>
                <button type="submit" disabled={savingBill} data-testid="save-bill-btn"
                  className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {savingBill ? <RefreshCw size={15} className="animate-spin" /> : <FileText size={15} />}
                  {savingBill ? 'Saving...' : 'Save Bill & Link to Cashflow'}
                </button>
              </form>
            </div>
          )}

          {/* Manual Entry Button */}
          {scanResult === null && !billForm.vendor_name && (
            <button
              onClick={() => setScanResult({})}
              className="flex items-center gap-2 border border-zinc-700 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 text-sm px-4 py-2.5 rounded-xl transition-colors"
            >
              <Plus size={14} /> Enter Bill Manually
            </button>
          )}

          {/* Bills List */}
          {bills.length > 0 && (() => {
            // iter163 — Filter saved bills client-side. Case-insensitive
            // substring on vendor + invoice no, with date range and
            // amount range.
            // iter166 — `q` ALSO matches against item-level lot_no so the
            // operator can locate a bill by lot number.
            const q = (billFilters.q || '').trim().toLowerCase();
            const vq = (billFilters.vendor || '').trim().toLowerCase();
            const lq = (billFilters.lot_no || '').trim().toLowerCase();
            // iter192 — search by product/item across master-detail rows AND
            // legacy single-line bills (which only have b.product_name).
            const pq = (billFilters.product || '').trim().toLowerCase();
            const df = billFilters.date_from || '';
            const dt = billFilters.date_to || '';
            const amin = parseFloat(billFilters.amount_min) || 0;
            const amax = parseFloat(billFilters.amount_max) || Infinity;
            const filteredBills = bills.filter(b => {
              const itemLots = (b.items || []).map(it => (it.lot_no || '').toLowerCase());
              const lotMatch = itemLots.some(l => l.includes(q));
              if (q && !((b.invoice_number || '').toLowerCase().includes(q)) && !lotMatch) return false;
              if (vq && !((b.vendor_name || '').toLowerCase().includes(vq))) return false;
              if (lq && !itemLots.some(l => l.includes(lq))) return false;
              if (pq) {
                const itemProducts = (b.items || []).map(it => (it.product_name || '').toLowerCase());
                const legacyProduct = (b.product_name || '').toLowerCase();
                const productMatch = itemProducts.some(p => p.includes(pq)) || legacyProduct.includes(pq);
                if (!productMatch) return false;
              }
              if (df && (b.invoice_date || '') < df) return false;
              if (dt && (b.invoice_date || '') > dt) return false;
              const t = parseFloat(b.total_amount || 0);
              if (t < amin || t > amax) return false;
              return true;
            });
            return (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">
                  Saved Purchase Bills ({filteredBills.length}{filteredBills.length !== bills.length ? ` of ${bills.length}` : ''})
                </h3>
                {/* iter163 — Filters */}
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="bill-filters">
                  <input data-testid="bill-filter-invoice"
                    value={billFilters.q} onChange={e => setBillFilters(f => ({ ...f, q: e.target.value }))}
                    placeholder="Invoice no or lot no…"
                    title="Searches invoice number AND item-level lot numbers"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-52 focus:outline-none focus:border-amber-500" />
                  <input data-testid="bill-filter-vendor"
                    value={billFilters.vendor} onChange={e => setBillFilters(f => ({ ...f, vendor: e.target.value }))}
                    placeholder="Vendor name…"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-44 focus:outline-none focus:border-amber-500" />
                  <input data-testid="bill-filter-lot"
                    value={billFilters.lot_no} onChange={e => setBillFilters(f => ({ ...f, lot_no: e.target.value }))}
                    placeholder="Lot no…"
                    title="Filter only by item-level lot number"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-32 focus:outline-none focus:border-amber-500" />
                  {/* iter192 — search by product/item across master-detail items */}
                  <input data-testid="bill-filter-product"
                    value={billFilters.product} onChange={e => setBillFilters(f => ({ ...f, product: e.target.value }))}
                    placeholder="Product / item…"
                    title="Filter by item product name (matches any line item in master-detail bills)"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-44 focus:outline-none focus:border-amber-500" />
                  <input data-testid="bill-filter-date-from"
                    type="date" value={billFilters.date_from} onChange={e => setBillFilters(f => ({ ...f, date_from: e.target.value }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
                  <span className="text-zinc-600 text-xs">→</span>
                  <input data-testid="bill-filter-date-to"
                    type="date" value={billFilters.date_to} onChange={e => setBillFilters(f => ({ ...f, date_to: e.target.value }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
                  <input data-testid="bill-filter-amount-min"
                    type="number" value={billFilters.amount_min} onChange={e => setBillFilters(f => ({ ...f, amount_min: e.target.value }))}
                    placeholder="₹ min"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-20 focus:outline-none focus:border-amber-500" />
                  <input data-testid="bill-filter-amount-max"
                    type="number" value={billFilters.amount_max} onChange={e => setBillFilters(f => ({ ...f, amount_max: e.target.value }))}
                    placeholder="₹ max"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-100 w-20 focus:outline-none focus:border-amber-500" />
                  {(billFilters.q || billFilters.vendor || billFilters.lot_no || billFilters.product || billFilters.date_from || billFilters.date_to || billFilters.amount_min || billFilters.amount_max) && (
                    <button data-testid="bill-filter-clear"
                      onClick={() => setBillFilters({ q: '', vendor: '', lot_no: '', product: '', date_from: '', date_to: '', amount_min: '', amount_max: '' })}
                      className="text-[10px] text-zinc-400 hover:text-zinc-100 px-2 py-1">Clear</button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto max-w-full">
              <table className="w-full text-sm min-w-[860px]">
                <thead><tr className="border-b border-zinc-800">
                  {(canViewCost
                    ? ['', 'Bill ID', 'Vendor', 'Invoice No.', 'Date', 'Lot No', 'Items', 'Qty', 'Total', 'Paid', 'Status', '']
                    : ['', 'Bill ID', 'Vendor', 'Invoice No.', 'Date', 'Lot No', 'Items', 'Qty', 'Status', '']).map((h, i) => (
                    <th key={i} className="text-left text-zinc-500 text-xs font-medium px-3 py-3">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredBills.map(bill => {
                    const outstanding = parseFloat(bill.total_amount || 0) - parseFloat(bill.paid_amount || 0);
                    const statusStyle = bill.payment_status === 'Paid'
                      ? 'bg-green-900/40 text-green-300 border-green-700'
                      : bill.payment_status === 'Partial'
                      ? 'bg-amber-900/40 text-amber-300 border-amber-700'
                      : 'bg-red-900/30 text-red-300 border-red-700';
                    const isExpanded = expandedBillId === bill.id;
                    const items = bill.items && bill.items.length > 0
                      ? bill.items
                      : [{
                          product_name: bill.product_name,
                          lot_no: bill.lot_no,
                          quantity: bill.quantity,
                          unit_price: bill.unit_price,
                          subtotal: bill.subtotal,
                          gst_rate: bill.gst_rate,
                          gst_amount: bill.gst_amount,
                          total_amount: bill.total_amount,
                          container_type: bill.container_type,
                        }];
                    return (
                      <React.Fragment key={bill.id}>
                      <tr className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${isExpanded ? 'bg-zinc-800/20' : ''}`} data-testid={`bill-row-${bill.id}`}>
                        <td className="px-2 py-3 align-top">
                          <button onClick={() => setExpandedBillId(isExpanded ? null : bill.id)}
                            data-testid={`bill-expand-${bill.id}`}
                            title={isExpanded ? 'Collapse items' : 'Show items (Lot No · Product · Qty · Rate · Subtotal)'}
                            className="p-1 rounded text-zinc-500 hover:text-amber-400 hover:bg-zinc-800">
                            {isExpanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                          </button>
                        </td>
                        <td className="px-3 py-3 font-mono text-amber-400 text-xs align-top">{bill.id}</td>
                        <td className="px-3 py-3 text-zinc-200 text-xs font-medium align-top">{bill.vendor_name}</td>
                        <td className="px-3 py-3 text-zinc-400 text-xs font-mono align-top">{bill.invoice_number || '—'}</td>
                        <td className="px-3 py-3 text-zinc-500 text-xs align-top">{bill.invoice_date || '—'}</td>
                        {/* iter167 — New Lot No column. For multi-item bills each
                            line's lot_no is stacked vertically so it aligns row-by-row
                            with the Items + Qty cells. */}
                        <td className="px-3 py-3 text-xs align-top" data-testid={`bill-lots-${bill.id}`}>
                          <div className="space-y-0.5">
                            {items.map((it, ii) => (
                              <div key={ii} className="font-mono text-[11px]">
                                {it.lot_no
                                  ? <span className="text-amber-300">{it.lot_no}</span>
                                  : <span className="text-zinc-700">—</span>}
                              </div>
                            ))}
                          </div>
                        </td>
                        {/* iter167 — Items: stack each product name per line so
                            user can read "Fried Onion / Pink Onion / Fresh Fried"
                            on three lines rather than the old "2 items" summary. */}
                        <td className="px-3 py-3 text-zinc-200 text-xs align-top" data-testid={`bill-items-${bill.id}`}>
                          <div className="space-y-0.5">
                            {items.map((it, ii) => (
                              <div key={ii} className="truncate max-w-[220px]" title={it.product_name}>
                                {it.product_name || '—'}
                              </div>
                            ))}
                          </div>
                        </td>
                        {/* iter167 — Qty per product, not bill-level total. */}
                        <td className="px-3 py-3 text-zinc-300 text-xs align-top" data-testid={`bill-qtys-${bill.id}`}>
                          <div className="space-y-0.5">
                            {items.map((it, ii) => (
                              <div key={ii} className="font-mono">
                                {it.quantity || 0} <span className="text-zinc-500">{it.container_type || ''}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        {canViewCost && <td className="px-3 py-3 font-mono text-amber-400 text-xs font-semibold align-top">{fmt(bill.total_amount)}</td>}
                        {canViewCost && <td className="px-3 py-3 font-mono text-green-400 text-xs align-top">{fmt(bill.paid_amount || 0)}</td>}
                        <td className="px-3 py-3 align-top">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusStyle}`}>{bill.payment_status || 'Unpaid'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`bill-pdf-btn-${bill.id}`}
                              onClick={() => downloadPdf(`${API}/purchases/bills/${bill.id}/pdf`, `Bill_${bill.invoice_number || bill.id}.pdf`)}
                              className="flex items-center gap-1 text-[10px] bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-amber-400 px-2 py-1 rounded-lg transition-colors"
                            >
                              <FileText size={10} /> PDF
                            </button>
                            {bill.payment_status !== 'Paid' && (
                              <button
                                data-testid={`pay-bill-btn-${bill.id}`}
                                onClick={() => openPaymentModal(bill)}
                                className="flex items-center gap-1 text-[10px] bg-green-900/30 hover:bg-green-800/40 border border-green-700 text-green-300 px-2 py-1 rounded-lg transition-colors"
                              >
                                <CreditCard size={10} /> Pay
                              </button>
                            )}
                            {/* iter155 — COA traceability: upload an image
                                of the Certificate of Analysis. The image
                                is also auto-mirrored onto the linked FIFO
                                lot (created when the bill was saved) so
                                it's visible everywhere the lot surfaces
                                (Inventory, Batch Production). */}
                            <label className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg cursor-pointer border ${bill.coa_image_base64 ? 'bg-green-900/30 hover:bg-green-800/40 border-green-700 text-green-300' : 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-400'}`}
                              data-testid={`coa-btn-${bill.id}`}
                              title={bill.coa_image_base64 ? `View COA (${bill.coa_filename || 'attached'})` : 'Upload Certificate of Analysis'}>
                              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const reader = new FileReader();
                                reader.onload = async (ev) => {
                                  const base64 = ev.target.result.split(',')[1];
                                  try {
                                    await axios.post(`${API}/purchases/bills/${bill.id}/coa`,
                                      { coa_image_base64: base64, filename: file.name }, { withCredentials: true });
                                    toast.success(`COA attached to bill ${bill.invoice_number || bill.id}`);
                                    // iter199 — was `fetchBills()` (undefined → silent crash
                                    // after a successful COA upload, leaving the UI stale).
                                    // The actual reload helper for this page is `fetchData()`.
                                    fetchData();
                                  } catch (err) {
                                    toast.error('COA upload failed: ' + (err.response?.data?.detail || err.message));
                                  }
                                };
                                reader.readAsDataURL(file);
                              }} />
                              <FileText size={10}/> COA {bill.coa_image_base64 ? '✓' : ''}
                            </label>
                            {bill.coa_image_base64 && (
                              <button onClick={() => {
                                // iter199 — Safe DOM construction (was document.write
                                // with string-concat HTML). The base64 string is
                                // ours from the DB, but the pattern itself is a
                                // static-analysis red flag, so we now build the
                                // <img> via createElement and set src via the
                                // image element's property setter — DOM property
                                // assignment can't be XSS'd the way innerHTML can.
                                const w = window.open('', '_blank');
                                if (!w) return;
                                w.document.title = 'COA Preview';
                                const img = w.document.createElement('img');
                                img.src = `data:image/jpeg;base64,${bill.coa_image_base64}`;
                                img.style.maxWidth = '100%';
                                img.style.display = 'block';
                                img.style.margin = '0 auto';
                                w.document.body.style.background = '#0a0a0a';
                                w.document.body.style.margin = '0';
                                w.document.body.appendChild(img);
                              }}
                                data-testid={`coa-view-${bill.id}`}
                                className="text-[10px] px-1.5 py-1 rounded-lg bg-blue-900/30 hover:bg-blue-800/40 border border-blue-700 text-blue-300"
                                title="Preview COA image">
                                👁
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                data-testid={`edit-bill-${bill.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingBill({
                                    ...bill,
                                    extra_charges: bill.extra_charges || [],
                                    items: (bill.items && bill.items.length > 0
                                      ? bill.items
                                      : [{
                                          product_name: bill.product_name || '',
                                          quantity: bill.quantity || '',
                                          container_type: bill.container_type || 'kg',
                                          unit_price: bill.unit_price || '',
                                          subtotal: bill.subtotal || '',
                                          gst_rate: bill.gst_rate || 5,
                                          gst_amount: bill.gst_amount || 0,
                                          total_amount: bill.total_amount || 0,
                                          hsn_code: bill.hsn_code || '',
                                          lot_no: bill.lot_no || '',
                                          receiving_date: bill.invoice_date || '',
                                        }]).map(it => ({ ...it })),
                                  });
                                }}
                                title="Edit bill (cascades to inventory, cashflow, voucher)"
                                className="p-1 rounded-lg bg-blue-900/30 text-blue-400 hover:text-blue-300 border border-blue-800/30">
                                <Pencil size={13} />
                              </button>
                            )}
                            {isAdmin && <button onClick={() => requestDelete(`/purchases/bills/${bill.id}`, bill.bill_number || bill.id, `Delete purchase bill and reverse data.`)} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                      {/* iter166 — Inline expand: items[] table with the
                           operator-requested column order: Lot No → Product
                           → Qty → Rate → Subtotal. */}
                      {isExpanded && (
                        <tr className="border-b border-zinc-800/50 bg-zinc-950/60" data-testid={`bill-items-expanded-${bill.id}`}>
                          <td colSpan={canViewCost ? 12 : 10} className="px-6 py-3">
                            <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Line items</div>
                            <div className="overflow-x-auto max-w-full">
                            <table className="w-full text-xs min-w-[640px]">
                              <thead>
                                <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
                                  <th className="text-left px-2 py-1.5 w-10">#</th>
                                  <th className="text-left px-2 py-1.5">Lot No</th>
                                  <th className="text-left px-2 py-1.5">Product</th>
                                  <th className="text-right px-2 py-1.5">Quantity</th>
                                  <th className="text-right px-2 py-1.5">Rate</th>
                                  <th className="text-right px-2 py-1.5">Subtotal</th>
                                  <th className="text-right px-2 py-1.5">GST</th>
                                  <th className="text-right px-2 py-1.5">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((it, ii) => (
                                  <tr key={ii} className="border-b border-zinc-800/30" data-testid={`bill-item-${bill.id}-${ii}`}>
                                    <td className="px-2 py-1.5 text-zinc-500">{ii + 1}</td>
                                    <td className="px-2 py-1.5">
                                      {it.lot_no
                                        ? <span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{it.lot_no}</span>
                                        : <span className="text-zinc-700">—</span>}
                                    </td>
                                    <td className="px-2 py-1.5 text-zinc-100 font-medium">{it.product_name || '—'}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-zinc-200">
                                      {it.quantity || 0} <span className="text-zinc-500">{it.container_type || ''}</span>
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{fmt(it.unit_price)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(it.subtotal)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400">
                                      {fmt(it.gst_amount)} <span className="text-zinc-600">({it.gst_rate || 0}%)</span>
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-mono text-amber-400 font-semibold">{fmt(it.total_amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            {bill.extra_charges && bill.extra_charges.length > 0 && (
                              <div className="mt-3 border-t border-zinc-800 pt-2">
                                <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Extra Charges</div>
                                <div className="flex flex-wrap gap-2">
                                  {bill.extra_charges.map((ec, ei) => (
                                    <span key={ei} className="text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-300 px-2 py-0.5 rounded" data-testid={`bill-extra-${bill.id}-${ei}`}>
                                      {ec.label}: <span className="font-mono text-amber-300">{fmt(ec.amount)}</span>
                                    </span>
                                  ))}
                                  <span className="text-[10px] bg-amber-900/30 border border-amber-700/40 text-amber-300 px-2 py-0.5 rounded font-semibold">
                                    Extras total: {fmt(bill.extras_total || bill.extra_charges.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0))}
                                  </span>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {tab === 'auto_po' && <AutoPoPanel />}

      {/* Vendor Modal */}
      <VendorModal
        open={showVendorModal}
        vendorForm={vendorForm}
        setVendorForm={setVendorForm}
        submitting={submitting}
        onClose={() => setShowVendorModal(false)}
        onSubmit={handleVendorSubmit}
      />

      {/* Payment Modal */}
      <PaymentModal
        paymentModal={paymentModal}
        setPaymentModal={setPaymentModal}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        submittingPayment={submittingPayment}
        setBills={setBills}
        onClose={() => setPaymentModal(null)}
        onSubmit={handleRecordPayment}
      />

      {/* Create PO Modal */}
      <CreatePOModal
        open={showPOModal}
        poForm={poForm}
        setPoForm={setPoForm}
        vendors={vendors}
        materials={materials}
        isAdmin={isAdmin}
        lookupItemCode={lookupItemCode}
        addPOItem={addPOItem}
        updatePOItem={updatePOItem}
        removePOItem={removePOItem}
        poSubtotal={poSubtotal}
        poTotal={poTotal}
        submitting={submitting}
        onClose={() => setShowPOModal(false)}
        onSubmit={handlePOSubmit}
      />

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {/* iter164 Sprint B — Edit Bill Modal */}
      <EditBillModal
        editingBill={editingBill}
        setEditingBill={setEditingBill}
        editBillSaving={editBillSaving}
        setEditBillSaving={setEditBillSaving}
        setBills={setBills}
        materials={materials}
        onClose={() => setEditingBill(null)}
      />

      {/* iter208 — Vendor advance auto-settle prompt */}
      {advancePrompt && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[80] p-4" data-testid="advance-prompt-modal">
          <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl w-full max-w-md p-5">
            <h3 className="font-heading font-bold text-amber-300 text-sm mb-1">
              Vendor has advance balance · settle this bill?
            </h3>
            <p className="text-zinc-400 text-xs mb-3">
              <span className="font-semibold text-zinc-200">{advancePrompt.vendorName}</span> has an
              on-account advance of <span className="font-mono text-amber-300">₹{advancePrompt.advanceBalance.toLocaleString('en-IN')}</span> with you.
              Bill <span className="font-mono">{advancePrompt.billNumber}</span> outstanding is
              <span className="font-mono text-red-300"> ₹{advancePrompt.billOutstanding.toLocaleString('en-IN')}</span>.
            </p>
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-xl p-3 mb-3 text-[11px] space-y-1">
              <div className="flex justify-between text-zinc-400"><span>Advance available</span><span className="font-mono">₹{advancePrompt.advanceBalance.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Bill outstanding</span><span className="font-mono">₹{advancePrompt.billOutstanding.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-amber-300 font-semibold border-t border-zinc-700 pt-1"><span>Will apply</span><span className="font-mono" data-testid="advance-apply-amount">₹{advancePrompt.suggested.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-green-400 text-[10px]">
                <span>Advance remaining after</span>
                <span className="font-mono">₹{(advancePrompt.advanceBalance - advancePrompt.suggested).toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setAdvancePrompt(null)}
                disabled={advanceApplying}
                data-testid="advance-skip-btn"
                className="flex-1 py-2 rounded-xl border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-800">
                Not now
              </button>
              <button
                disabled={advanceApplying}
                data-testid="advance-apply-btn"
                onClick={async () => {
                  setAdvanceApplying(true);
                  try {
                    const r = await axios.post(`${API}/purchases/bills/${advancePrompt.billId}/apply-advance`, {}, { withCredentials: true });
                    toast.success(`₹${r.data.applied.toLocaleString('en-IN')} applied · bill ${r.data.payment_status}`);
                    setAdvancePrompt(null);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to apply advance');
                  } finally { setAdvanceApplying(false); }
                }}
                className="flex-1 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs disabled:opacity-50">
                {advanceApplying ? 'Applying…' : `Apply ₹${advancePrompt.suggested.toLocaleString('en-IN')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Auto-PO Panel — low-stock shortages + draft POs generator
// ═══════════════════════════════════════════════════════════════════════════
function AutoPoPanel() {
  const [shortages, setShortages] = React.useState([]);
  const [drafts, setDrafts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API}/auto-po/shortages`, { withCredentials: true }),
        axios.get(`${API}/auto-po/drafts`, { withCredentials: true }),
      ]);
      setShortages(s.data.shortages || []);
      setDrafts(d.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm(`Generate draft POs for ${shortages.length} low-stock item${shortages.length === 1 ? '' : 's'}? They'll be grouped by vendor and saved as Draft so you can review/edit/send.`)) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/auto-po/run`, {}, { withCredentials: true });
      toast.success(`Created ${res.data.drafts_created} draft PO${res.data.drafts_created === 1 ? '' : 's'} from ${res.data.shortages} shortage${res.data.shortages === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="text-zinc-400 text-sm py-6">Loading auto-PO…</div>;

  return (
    <div className="space-y-4" data-testid="auto-po-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-zinc-50 font-heading font-semibold text-sm flex items-center gap-2">
            <ShoppingCart size={14} className="text-amber-400" /> Auto-PO Low Stock Watchdog
          </h3>
          <p className="text-zinc-500 text-xs mt-0.5">
            Scans raw materials nightly at 09:30 IST and drafts POs per vendor. Run manually any time.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running || shortages.length === 0}
          className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs disabled:opacity-50 flex items-center gap-1.5"
          data-testid="auto-po-run-btn"
        >
          <RefreshCw size={12} /> {running ? 'Working…' : `Generate ${shortages.length} draft PO${shortages.length === 1 ? '' : 's'}`}
        </button>
      </div>

      {/* Shortages */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 text-zinc-300 text-xs font-semibold flex items-center gap-2">
          <AlertCircle size={12} className="text-red-400" /> Current Shortages ({shortages.length})
        </div>
        {shortages.length === 0 ? (
          <div className="px-4 py-8 text-center text-zinc-500 text-xs">
            All raw materials are above minimum stock.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-zinc-500 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-right px-4 py-2">Current</th>
                <th className="text-right px-4 py-2">Min Stock</th>
                <th className="text-right px-4 py-2">Shortage %</th>
                <th className="text-right px-4 py-2">Suggested Qty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {shortages.map(s => (
                <tr key={s.id} className="hover:bg-zinc-800/40" data-testid={`shortage-${s.id}`}>
                  <td className="px-4 py-2 text-zinc-200">{s.name}</td>
                  <td className="px-4 py-2 text-right font-mono text-red-300">{s.current_stock} {s.unit}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400">{s.min_stock} {s.unit}</td>
                  <td className="px-4 py-2 text-right font-mono text-red-300 font-semibold">{s.shortage_pct}%</td>
                  <td className="px-4 py-2 text-right font-mono text-amber-300">{s.suggested_qty} {s.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drafts */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 text-zinc-300 text-xs font-semibold flex items-center gap-2">
          <FileText size={12} className="text-blue-400" /> Auto-Generated Draft POs ({drafts.length})
        </div>
        {drafts.length === 0 ? (
          <div className="px-4 py-6 text-center text-zinc-500 text-xs">
            No drafts yet. Click "Generate" above or wait for the 09:30 IST scheduler tick.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {drafts.map(po => (
              <div key={po.id} className="px-4 py-3 hover:bg-zinc-800/40" data-testid={`auto-draft-${po.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                  <div>
                    <span className="text-zinc-200 text-sm font-semibold">{po.vendor_name || 'Unassigned Vendor'}</span>
                    <span className="text-zinc-500 text-[11px] ml-2">· {po.id}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border bg-blue-900/30 text-blue-300 border-blue-700 ml-2">Draft</span>
                  </div>
                  <div className="text-sm font-mono text-amber-300 font-semibold">₹{(po.total_amount || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mb-1.5">
                  {po.items?.length || 0} item{po.items?.length === 1 ? '' : 's'} · {new Date(po.created_at).toLocaleString('en-IN')}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(po.items || []).map((it, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                      <ItemCodeLabel name={it.material_name} /> · {it.quantity}{it.unit} · ₹{it.unit_price}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

